import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { getRequestInstance } from '@@/plugin-request/request';
import { clearTokens, getAccessToken, getRefreshToken, setAccessToken } from './utils/auth';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

// 兼容：后端新 Result<T> 返回结构
interface BackendResultStructure {
  isSuccess: boolean;
  value?: any;
  code?: number;
  message?: string;
}

const LOGIN_PATH = '/user/login';
const REFRESH_URL = '/api/admin/auth/refresh';
const LOGIN_URL = '/api/admin/auth/login/account';

let refreshingTask: Promise<string | null> | null = null;

function redirectToLogin() {
  if (window.location.pathname === LOGIN_PATH) return;
  const { search, pathname } = window.location;
  const searchParams = new URLSearchParams({ redirect: pathname + search });
  window.location.href = `${LOGIN_PATH}?${searchParams.toString()}`;
}

async function refreshAccessTokenOnce(): Promise<string | null> {
  if (refreshingTask) return refreshingTask;

  refreshingTask = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;

    try {
      const resp = await fetch(REFRESH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!resp.ok) return null;
      const json = (await resp.json().catch(() => null)) as
        | ({ accessToken?: string; success?: boolean } & Partial<ResponseStructure>)
        | ({ isSuccess?: boolean; value?: { accessToken?: string } } & Partial<BackendResultStructure>)
        | null;

      const accessToken =
        (typeof (json as any)?.accessToken === 'string' ? (json as any).accessToken : null) ??
        (typeof (json as any)?.value?.accessToken === 'string' ? (json as any).value.accessToken : null);

      if (typeof accessToken !== 'string' || !accessToken) return null;

      setAccessToken(accessToken);
      return accessToken;
    } catch {
      return null;
    }
  })().finally(() => {
    refreshingTask = null;
  });

  return refreshingTask;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const r = res as any;
      // 约定：后端新 Result<T>（isSuccess/value/message/code）不在这里抛出业务异常，
      // 交给各业务调用方（services/pages）按自身场景处理。
      if (typeof r?.isSuccess === 'boolean') return;

      const { success, data, errorCode, errorMessage, showType } = r as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        if (error.response.status !== 401) {
          message.error(`Response status:${error.response.status}`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const accessToken = getAccessToken();
      if (!accessToken) {
        return config;
      }

      return {
        ...config,
        headers: {
          ...(config.headers ?? {}),
          Authorization: `Bearer ${accessToken}`,
        },
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as any;

      // 旧响应格式才在此处提示；新 Result<T> 由业务侧自行提示。
      if (data?.success === false) {
        message.error('请求失败！');
      }

      return response;
    },

    // 全局：401 自动刷新 access token + 重放原请求（并发只刷新一次）
    [
      (response) => response,
      async (error: any) => {
        const status = error?.response?.status;
        const originalConfig = error?.config as (RequestOptions & { __isRetry?: boolean }) | undefined;
        const url = (originalConfig as any)?.url as string | undefined;

        // 非 401 / 无法重试 / 登录页 不处理
        if (status !== 401 || !originalConfig || !url || window.location.pathname === LOGIN_PATH) {
          return Promise.reject(error);
        }

        // 不对 refresh/login 自己做重试，避免死循环
        if (url.includes(REFRESH_URL) || url.includes(LOGIN_URL)) {
          clearTokens();
          redirectToLogin();
          return Promise.reject(error);
        }

        if ((originalConfig as any).__isRetry) {
          clearTokens();
          redirectToLogin();
          return Promise.reject(error);
        }

        const newToken = await refreshAccessTokenOnce();
        if (!newToken) {
          clearTokens();
          redirectToLogin();
          return Promise.reject(error);
        }

        (originalConfig as any).__isRetry = true;
        originalConfig.headers = {
          ...(originalConfig.headers ?? {}),
          Authorization: `Bearer ${newToken}`,
        };

        // 用同一个 axios instance 重放
        return getRequestInstance().request(originalConfig as any);
      },
    ],
  ],
};
